import api from "./axios";

/**
 * GLOBAL SEARCH — THE INTEGRATION BOUNDARY (Part 1 §3, §3A).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE ENDPOINT EXISTS NOW — PART 6b · PHASE 6 (GLOBAL SEARCH = A, LOCKED)
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * `GET /search` is the approved backend this file was written to wait for, and wiring the path
 * below was the only frontend change it needed — exactly as the note on that constant predicted.
 *
 * EVERY RULE THIS SEAM DECLARED IS NOW ENFORCED SERVER-SIDE, not merely honoured here:
 * `globalSearchService` scopes each family with the OWNING module's own helper (13T's list filter,
 * Part 3's read tier, 13X's task-scoped property visibility, PD11's lawyer rule), so a record the
 * caller may not reach is never queried, never returned, and indistinguishable from one that does
 * not exist. The result shape below is unchanged, because it was chosen to match what already
 * existed rather than to be new.
 *
 * The paragraph that follows is kept as the record of why this file was a seam for so long.
 *
 * The whole repository contained, until this phase, exactly ONE server-side search:
 * `GET /sales-payments/search` (`functions/src/services/salesSearchService.js`), and it is a
 * REGISTRATION & SALES DEPARTMENT search — `assertSearchActor` refuses every actor who is neither
 * the Master Admin nor a member of that department, and every group it returns is a sales record.
 * It is not a global search and it cannot be turned into one by calling it from a global control.
 *
 * So this module is the SEAM, not the implementation. It declares the contract the eventual endpoint
 * must satisfy, refuses to pretend, and gives `GlobalSearch.jsx` a single place to bind to on the
 * day that endpoint is approved and built.
 *
 * ── WHAT IS DELIBERATELY NOT DONE HERE ──────────────────────────────────────
 * 1. No fabricated results. `searchGlobal` never resolves with invented data.
 * 2. No API laundering. It does NOT call `/sales-payments/search`, `/properties`, `/tasks`,
 *    `/customers` or any other list endpoint and relabel the answer "global search". Federating
 *    list endpoints client-side would put the RESULT SET together in React, which is precisely the
 *    client-side authorization §3 forbids — each list applies its own scope, but which families a
 *    given actor may search across, and what a hit may reveal, is a decision no existing endpoint
 *    has made.
 * 3. No scope parameter, in any spelling. See below.
 *
 * ── THE ONE PARAMETER RULE (§3) ─────────────────────────────────────────────
 * `searchGlobal` accepts a query string and NOTHING ELSE, and builds its request from that alone.
 * There is no `department`, no `scope`, no `includeAll`, no `searchAll`, no entity-family list — so
 * a caller cannot widen authorization even by mistake, and a hostile `?department=all` appended by
 * hand has nothing on this side to bind to. Who the caller is decides what they find, on the server,
 * on every call. That is the same rule `salesSearchService` already states and enforces, restated
 * here so the eventual endpoint inherits it rather than re-deciding it.
 *
 * ── THE RESULT SHAPE IS NOT NEW EITHER ──────────────────────────────────────
 * It is the shape `salesSearchService` already returns and `pages/salesPayment/SalesSearch.jsx`
 * already renders, so the eventual endpoint follows an established repository convention instead of
 * inventing a second one:
 *
 *   {
 *     query: string,
 *     total: number,
 *     groups: [{ key, label, items: [{ id, code, label, detail, to }] }],
 *     scope?: string          // optional, e.g. "own" — explains a thin result set to its reader
 *   }
 *
 * `to` is an in-application path. It is a DOOR, not a bypass: opening it runs the destination's own
 * `RoleRoute` guard and its own server-side read, exactly as if the path had been typed.
 */

/**
 * The path of the approved global-search endpoint, or `null` while none exists.
 *
 * IT IS `null` ON PURPOSE. Setting it to a plausible-looking string is how a UI starts claiming a
 * capability nobody built: the requests would 404, the panel would show an error, and the error
 * would look like a bug rather than like the missing prerequisite it is. Wiring the real path here
 * — once the endpoint is approved and implemented — is the ONLY frontend change global search needs.
 */
export const GLOBAL_SEARCH_ENDPOINT = "/search";

/**
 * Two characters, the same floor `salesSearchService` enforces server-side. A single letter matches
 * most of the database and answers nothing.
 */
export const MIN_QUERY_LENGTH = 2;

/** Global search is available only when a real endpoint has been wired above. */
export const isGlobalSearchAvailable = () =>
  typeof GLOBAL_SEARCH_ENDPOINT === "string" && GLOBAL_SEARCH_ENDPOINT.length > 0;

/**
 * "The backend does not exist yet" is a DIFFERENT STATE from "the search failed", and the panel
 * renders it differently: a missing prerequisite is not something a Retry button can fix, so
 * offering one would be a lie. A distinct error type is what lets the component tell them apart
 * without string-matching a message.
 */
export class GlobalSearchUnavailableError extends Error {
  constructor() {
    super("Global search is not available yet.");
    this.name = "GlobalSearchUnavailableError";
    this.unavailable = true;
  }
}

/**
 * Run a global search.
 *
 * @param {string} q the reader's text — the ONLY input, by design
 * @returns {Promise<{query: string, total: number, groups: Array, scope?: string}>}
 * @throws {GlobalSearchUnavailableError} while no approved endpoint exists
 */
export const searchGlobal = (q) => {
  if (!isGlobalSearchAvailable()) return Promise.reject(new GlobalSearchUnavailableError());
  // `params` carries the query and nothing else — no scope key exists to be widened.
  return api.get(GLOBAL_SEARCH_ENDPOINT, { params: { q } }).then((r) => r.data.data);
};
